import React, { useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Alert,
  Spinner,
  ProgressBar,
} from "react-bootstrap";
import { useParams } from "react-router-dom";

// Components
import MobileHeader from "./components/navigation/MobileHeader";
import DesktopHeader from "./components/navigation/DesktopHeader";
import MobileProgress from "./components/navigation/MobileProgress";
import StepsNavigation from "./components/navigation/StepsNavigation";
import NavigationFooter from "./components/navigation/NavigationFooter";
import StepContent from "./components/steps/StepContent";

// Hooks
import { useFormValidation } from "./hooks/useFormValidation";
import { useDraftManager } from "./hooks/useDraftManager";
import { usePublicationManager } from "./hooks/usePublicationManager";
import { useAutoSave } from "./hooks/useAutoSave";

// Constants
import { TOTAL_STEPS, VALIDATION_MESSAGES } from "./constants/formConstants";

// Styles
import "./marketplace.css";
import "./MarketplacePublicationWizard.css";


const MarketplacePublicationForm = () => {
  const { id } = useParams();
  const isEditing = !!id; // Check if we're in edit mode

  // Custom hooks
  const {
    currentStep,
    setCurrentStep,
    setCurrentStepDirect,
    register,
    handleSubmit,
    errors,
    control,
    setValue,
    getValues,
    watch,
    reset,
    trigger,
    nextStep,
    prevStep,
  } = useFormValidation();

  const {
    draftLoading,
    saveDraft,
    loadDraft,
    clearDraft,
    handleSaveDraft,
  } = useDraftManager(id, isEditing, getValues, reset, currentStep);

  const {
    loading,
    saving,
    error,
    setError,
    loadPublication,
    submitPublication,
    handleCancel,
  } = usePublicationManager(id, isEditing, reset, clearDraft);
  // Watch form values for auto-save
  const formValues = watch();
  
  // Auto-save functionality
  useAutoSave(formValues, saveDraft, isEditing);

  // Load publication data for editing or draft for new publications
  useEffect(() => {
    const initializeForm = async () => {
      console.log('🚀 Inicializando formulário...', { isEditing, id });
      
      if (isEditing && id) {
        try {
          console.log('📖 Carregando publicação para edição...', { id });
          const publicationData = await loadPublication();
          console.log('✅ Publicação carregada com sucesso:', publicationData);
        } catch (error) {
          console.error('❌ Falha ao carregar publicação:', error);
        }
      } else {
        try {
          const initialStep = await loadDraft();
          console.log('🎯 Initial step from draft:', initialStep, 'Current step:', currentStep);
          if (initialStep && initialStep !== currentStep && initialStep > 1) {
            console.log('📍 Setting current step to:', initialStep);
            setCurrentStepDirect(initialStep);
          }
        } catch (error) {
          console.error('Failed to load draft:', error);
        }
      }
    };
    
    initializeForm();
  }, [id, isEditing]); // Mantemos apenas id e isEditing para evitar loops infinitos





  // Watch form values
  const formData = formValues;

  // Handle final form submission
  const onFormSubmit = async (data) => {
    await submitPublication(data);
  };



  if (loading) {
    return (
      <Container fluid className="py-4">
        <Card>
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <h5>{VALIDATION_MESSAGES.LOADING_PUBLICATION}</h5>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <div className="mpf-wizard-container">
      {/* Mobile Header */}
      <MobileHeader 
        isEditing={isEditing}
        onCancel={handleCancel}
        onSaveDraft={handleSaveDraft}
        draftLoading={draftLoading}
      />
      
      {/* Mobile Progress */}
      <MobileProgress currentStep={currentStep} />
      
      <Container fluid className="mpf-main-container py-4">
        {/* Desktop Header */}
        <DesktopHeader
          isEditing={isEditing}
          onCancel={handleCancel}
          onSaveDraft={handleSaveDraft}
          draftLoading={draftLoading}
        />
        
        {/* Error Alert */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
            <Alert.Heading>
              <i className="las la-exclamation-triangle me-2" />
              Erro
            </Alert.Heading>
            {error}
          </Alert>
        )}
        
        {/* Desktop Progress Bar */}
        <div className="d-none d-md-block mb-4">
          <ProgressBar
            now={(currentStep / TOTAL_STEPS) * 100}
            className="mpf-progress-bar-desktop"
            style={{ height: "8px" }}
          />
        </div>
        
        {/* Desktop Steps Navigation */}
        <StepsNavigation 
          currentStep={currentStep}
          onStepClick={setCurrentStep}
        />

        {/* Main Form Content */}
        <Card className="mpf-form-card">
          <Card.Body>
            <Form onSubmit={handleSubmit(onFormSubmit)}>
              {/* Step Content */}
              <div className="mpf-form-content">
                <StepContent
                  currentStep={currentStep}
                  formData={formData}
                  register={register}
                  setValue={setValue}
                  control={control}
                  errors={errors}
                  saving={saving}
                  trigger={trigger}
                />
              </div>
              
              {/* Navigation Footer */}
              <NavigationFooter
                currentStep={currentStep}
                onPrevStep={prevStep}
                onNextStep={nextStep}
                onSubmit={handleSubmit(onFormSubmit)}
                saving={saving}
                isEditing={isEditing}
              />
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default MarketplacePublicationForm;